import { useInterval } from 'hooks/usePolling';
import { useCallback, useEffect, useState } from 'react';
import { scheduleAttainementScreenServices } from 'services/ScheduleAttainmentScreen/scheduleAttainementScreenServices';
import { getDateWithTime } from 'utils/dateUtilities';
import { getJitteredInterval } from 'utils/pollingUtility';
import { commaSeparatedNumbers } from '../../utils/commaSeparatedNumbers';
import { RoomData } from './components/ScheduleAttainmentByRoomScreen/scheduleAttainmentbyRoom.type';
import { ScheduledRoomData } from './components/ScheduledRooms/scheduleRoom.type';
import {toast} from 'react-toastify';

export const useScheduleAttainment = () => {
  const [loading, setLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [scheduledRooms, setScheduledRooms] = useState<ScheduledRoomData[]>([]);
  const [scheduleAttainmentByRoomFirstShift, setScheduleAttainmentByRoomFirstShift] = useState<
    RoomData[]
  >([]);
  const [scheduleAttainmentByRoomSecondShift, setScheduleAttainmentByRoomSecondShift] = useState<
    RoomData[]
  >([]);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  const fetchScheduledRooms = async () => {
    try {
      const response = await scheduleAttainementScreenServices.getScheduledRooms();
      if (response.success) {
        setScheduledRooms(response.data);
      }
    } catch (error) {
      if (environment === 'development') {
        console.error('Error in fetchScheduledRooms:', error);
      }
      toast.error('Error in fetching Scheduled Rooms!');
    }
  };

  const fetchScheduleAttainmentByRoom = async () => {
    try {
      const response = await scheduleAttainementScreenServices.getScheduleAttainmentByRoom();
      if (response.success) {
        setScheduleAttainmentByRoomFirstShift(response.data.First);
        setScheduleAttainmentByRoomSecondShift(response.data.Second);
      }
    } catch (error) {
      if (environment === 'development') {
        console.error('Error in fetchScheduleAttainmentByRoom:', error);
      }
      toast.error('Error in fetching Schedule Attainment by Room!');
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchScheduledRooms(), fetchScheduleAttainmentByRoom()]);
    } catch (error) {
      if (environment === 'development') {
        console.error('Error fetching all data:', error);
      }
      toast.error('Error fetching schedule attainment data!');
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling method (without loading indicator)
  const pollAllData = useCallback(async () => {
    setIsPolling(true);
    try {
      const [scheduledRoomsResponse, scheduleAttainmentResponse] = await Promise.all([
        scheduleAttainementScreenServices.getScheduledRooms(),
        scheduleAttainementScreenServices.getScheduleAttainmentByRoom()
      ]);

      // Update scheduled rooms only if data has changed
      if (scheduledRoomsResponse.success) {
        const newScheduledRooms = scheduledRoomsResponse.data;
        if (JSON.stringify(scheduledRooms) !== JSON.stringify(newScheduledRooms)) {
          setScheduledRooms(newScheduledRooms);
        }
      }

      // Update schedule attainment data only if data has changed
      if (scheduleAttainmentResponse.success) {
        const newFirstShift = scheduleAttainmentResponse.data.First;
        const newSecondShift = scheduleAttainmentResponse.data.Second;

        if (JSON.stringify(scheduleAttainmentByRoomFirstShift) !== JSON.stringify(newFirstShift)) {
          setScheduleAttainmentByRoomFirstShift(newFirstShift);
        }

        if (
          JSON.stringify(scheduleAttainmentByRoomSecondShift) !== JSON.stringify(newSecondShift)
        ) {
          setScheduleAttainmentByRoomSecondShift(newSecondShift);
        }
      }
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for schedule attainment data!');
    } finally {
      setIsPolling(false);
    }
  }, [scheduledRooms, scheduleAttainmentByRoomFirstShift, scheduleAttainmentByRoomSecondShift]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await pollAllData();
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling interval:', error);
      }
      toast.error('Error during polling interval for schedule attainment data!');
    }
  }, POLLING_INTERVAL);

  const handlePrint = async () => {
    setLoading(true);
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        return;
      }

      // Create scheduled rooms map for lookup
      const scheduledRoomsMap = new Map(scheduledRooms.map(room => [room.room_long, room]));

      // Calculate totals for first shift - sum all values, treat "-" as 0
      const firstShiftTotals = scheduleAttainmentByRoomFirstShift.reduce(
        (acc, room) => {
          // Convert "-" to 0, otherwise convert to number
          const jobsValue = room.jobs_required === '-' ? 0 : Number(room.jobs_required) || 0;
          const bottlesValue = room.bottles_required === '-' ? 0 : Number(room.bottles_required) || 0;
          
          return {
            jobs_required: acc.jobs_required + jobsValue,
            bottles_required: acc.bottles_required + bottlesValue
          };
        },
        { jobs_required: 0, bottles_required: 0 }
      );

      // Calculate totals for second shift - sum all values, treat "-" as 0
      const secondShiftTotals = scheduleAttainmentByRoomSecondShift.reduce(
        (acc, room) => {
          // Convert "-" to 0, otherwise convert to number
          const jobsValue = room.jobs_required === '-' ? 0 : Number(room.jobs_required) || 0;
          const bottlesValue = room.bottles_required === '-' ? 0 : Number(room.bottles_required) || 0;
          
          return {
            jobs_required: acc.jobs_required + jobsValue,
            bottles_required: acc.bottles_required + bottlesValue
          };
        },
        { jobs_required: 0, bottles_required: 0 }
      );

      const printContent = `
        <html>
          <head>
            <title>Schedule Attainment Report</title>
            <style>
              @page {
                size: A4 landscape;
                margin: 1cm;
              }
              body { 
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                font-size: 12px;
              }
              .print-container { 
                padding: 0;
              }
              .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 20px;
                padding: 0 10px;
              }
              .date {
                color: #4D2E6D;
                font-weight: 600;
                font-size: 14px;
              }
              .purple-header {
                background: #4d2e6d;
                color: white;
                padding: 15px;
                text-align: center;
                font-size: 16px;
                font-weight: 600;
                margin-bottom: 0;
              }
              .tables-container {
                display: flex;
                gap: 20px;
                width: 100%;
              }
              .shift-section {
                flex: 1;
                border: 1px solid #ede7f6;
                border-radius: 0 0 8px 8px;
              }
              .shift-header {
                background: #4d2e6d;
                color: white;
                font-weight: 600;
                font-size: 18px;
                padding: 12px 0;
                text-align: center;
                margin: 0;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 0;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
                font-size: 11px;
              }
              th {
                background-color: #ede7f6;
                color: #4d2e6d;
                font-weight: 600;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .total-row {
                background-color: #f8f5fc !important;
                border-top: 2px solid #4d2e6d !important;
              }
              .total-value {
                color: #4d2e6d;
                font-weight: 700;
              }
              .center {
                text-align: center;
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <div class="header">
                <div></div>
                <div class="date">Date: ${getDateWithTime()}</div>
              </div>
              
              <div class="purple-header">
                Schedule Attainment Calculator by Room
              </div>

              <div class="tables-container">
                <div class="shift-section">
                  <div class="shift-header">First Shift</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Room</th>
                        <th>Jobs Required</th>
                        <th>Bottles Required</th>
                        <th class="center">Is Room Running</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${scheduleAttainmentByRoomFirstShift
                        .map(room => {
                          const scheduledRoom = scheduledRoomsMap.get(room.room_long);
                          const isRunning = scheduledRoom?.First || 'N';
                          
                          // Display "-" for jobs and bottles when room is not running or API returns "-"
                          const jobsDisplay = isRunning === 'N' || room.jobs_required === '-' ? '-' : commaSeparatedNumbers(room.jobs_required);
                          const bottlesDisplay = isRunning === 'N' || room.bottles_required === '-' ? '-' : commaSeparatedNumbers(room.bottles_required);
                          
                          return `
                            <tr>
                              <td>${room.room_long}</td>
                              <td>${jobsDisplay}</td>
                              <td>${bottlesDisplay}</td>
                              <td class="center">${isRunning}</td>
                            </tr>
                          `;
                        })
                        .join('')}
                      <tr class="total-row">
                        <td class="total-value">Total</td>
                        <td class="total-value">${commaSeparatedNumbers(firstShiftTotals.jobs_required)}</td>
                        <td class="total-value">${commaSeparatedNumbers(firstShiftTotals.bottles_required)}</td>
                        <td class="center">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="shift-section">
                  <div class="shift-header">Second Shift</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Room</th>
                        <th>Jobs Required</th>
                        <th>Bottles Required</th>
                        <th class="center">Is Room Running</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${scheduleAttainmentByRoomSecondShift
                        .map(room => {
                          const scheduledRoom = scheduledRoomsMap.get(room.room_long);
                          const isRunning = scheduledRoom?.Second || 'N';
                          
                          // Display "-" for jobs and bottles when room is not running or API returns "-"
                          const jobsDisplay = isRunning === 'N' || room.jobs_required === '-' ? '-' : commaSeparatedNumbers(room.jobs_required);
                          const bottlesDisplay = isRunning === 'N' || room.bottles_required === '-' ? '-' : commaSeparatedNumbers(room.bottles_required);
                          
                          return `
                            <tr>
                              <td>${room.room_long}</td>
                              <td>${jobsDisplay}</td>
                              <td>${bottlesDisplay}</td>
                              <td class="center">${isRunning}</td>
                            </tr>
                          `;
                        })
                        .join('')}
                      <tr class="total-row">
                        <td class="total-value">Total</td>
                        <td class="total-value">${commaSeparatedNumbers(secondShiftTotals.jobs_required)}</td>
                        <td class="total-value">${commaSeparatedNumbers(secondShiftTotals.bottles_required)}</td>
                        <td class="center">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } catch (error) {
      if (environment === 'development') {
        console.error('Error preparing print data:', error);
      }
      toast.error('Error preparing print data!');
    } finally {
      setLoading(false);
    }
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await fetchAllData();
  };

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    loading,
    scheduledRooms,
    scheduleAttainmentByRoomFirstShift,
    scheduleAttainmentByRoomSecondShift,
    handlePrint,
    refreshData,
    isPolling
  };
};
